import React, { useCallback, useEffect, useMemo } from "react";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Alert from "@mui/material/Alert";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { setPrevState } from "../../../../../../../../util/common";
import { JOB_PURPOSE_CONVERSION } from "../../../../../utils/JobData";
import BrowseInput from "./BrowseInput";
import BrowserAsTarget from "./BrowserAsTarget";
import TableAsTarget from "./TableAsTarget";

const sqlJobs = ["snowflake", "salesforce", "veevavault", "mysql"];
const browserAsTargets = ["s3", "sftp", "azure"];

const DestinationPropertiesForm = ({
  loading,
  exchangeJobState,
  setExchangeJobState,
  previousState,
  destinationType,
}) => {
  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography
          sx={{
            fontWeight: "bold",
          }}
        >
          Destination Table Properties -
        </Typography>
      </Grid>
      <TableAsTarget
        exchangeJobState={exchangeJobState}
        setExchangeJobState={setExchangeJobState}
        destinationType={destinationType}
        previousState={previousState}
        loading={loading}
      />
    </Grid>
  );
};

const DestinationForm = ({
  setExchangeJobState,
  exchangeJobState,
  previousState,
  fieldLoading,
  loadSchemasClusters,
}) => {
  const { destination, locationList, source, sourceType, sourcePath, jobPurpose, singleFile } = exchangeJobState;

  const destinationType = useMemo(
    () => locationList.find(({ Stage_id }) => Stage_id === destination)?.Detail?.type?.toLowerCase() || "",
    [destination, locationList]
  );

  const $destinationList = useMemo(() => {
    if (!locationList.length) return [];

    if (jobPurpose === JOB_PURPOSE_CONVERSION) {
      return locationList.filter(({ Detail }) => ["s3", "azure"].includes(Detail?.type?.toLowerCase()));
    } else {
      // add acceptable types based on sourceType and source
      const lowerSourceType = sourceType.toLowerCase();
      const acceptableTypes = new Set(["s3"]);

      if (source === "SNOWFLAKE") {
        acceptableTypes.add("salesforce");
        acceptableTypes.add("veevavault");
      }
      if (lowerSourceType !== "snowflake") acceptableTypes.add("snowflake");
      if (lowerSourceType === "s3")
        ["mysql", "sftp", "salesforce", "veevavault"].forEach((type) => acceptableTypes.add(type));

      let filteredList = locationList.filter(({ Detail, Stage_id }) => {
        const detailType = Detail?.type?.toLowerCase();

        if (!acceptableTypes.has(detailType)) return false;
        if (sqlJobs.includes(lowerSourceType) || lowerSourceType === "") return detailType !== "snowflake";
        if (lowerSourceType === "s3") {
          return detailType === "snowflake" || ["s3", "mysql", "sftp", "salesforce", "veevavault"].includes(detailType);
        }
        if (lowerSourceType !== "s3" && detailType === "snowflake") return Stage_id === "SNOWFLAKE";
        return true;
      });

      return filteredList;
    }
  }, [source, locationList, jobPurpose, sourceType]);

  useEffect(() => {
    if (!$destinationList.length || !destination) return;

    if (!$destinationList.find(({ Stage_id }) => Stage_id === destination)) {
      setPrevState(setExchangeJobState, {
        destination: "",
        targetPath: "",
      });
    }
  }, [destination, $destinationList, setExchangeJobState]);

  const $hideProperties = useMemo(
    () => !!(["snowflake", "mysql"]?.includes(destinationType) && sourcePath && sourcePath.split(",/").length > 1),
    [destinationType, sourcePath]
  );

  const handleConnectionChange = useCallback(
    (e) => {
      const selectedLocation = $destinationList.find((location) => location.Stage_id === e.target.value).Detail;

      loadSchemasClusters(selectedLocation.type?.toLowerCase(), e.target.value);

      setExchangeJobState((prev) => {
        return {
          ...prev,
          ...(e.target.value !== prev.destination
            ? {
                targetPath: "",
                desinationContainer: "",
                schemaName: "",
                tableName: "",
                externalIdFieldName: "",
              }
            : {}),
          destination: e.target.value,
          fileType: selectedLocation.type?.toLowerCase() === "mysql" ? "csv" : prev.fileType,
          desinationContainer: selectedLocation.container,
          isFieldsConfigured:
            e.target.value !== previousState.destination ? false : exchangeJobState.isFieldsConfigured,
          ...(destination === "SNOWFLAKE" ? { targetPath: "", desinationContainer: "" } : {}),
        };
      });
    },
    [
      $destinationList,
      destination,
      exchangeJobState.isFieldsConfigured,
      loadSchemasClusters,
      previousState.destination,
      setExchangeJobState,
    ]
  );

  return (
    <Grid container direction="row" spacing={2}>
      {browserAsTargets.includes(destinationType) && (
        <Grid sx={{ color: "#ed6c02", paddingBottom: "10px" }} size={12}>
          Export location
        </Grid>
      )}
      <Grid size={12}>
        <FormControl
          sx={{
            width: { xs: "100%", sm: "300px" },
            m: 1,
            ml: 0,
          }}
          error={$hideProperties}
        >
          <InputLabel required>Connection</InputLabel>
          <Select
            label="Connection"
            value={destination}
            onChange={handleConnectionChange}
            required
            MenuProps={{ sx: { height: "220px" } }}
          >
            {$destinationList.map((option) => (
              <MenuItem
                key={option.Stage_id}
                value={option.Stage_id}
                disabled={option.Stage_id === "SNOWFLAKE" && !source && source === "SNOWFLAKE"}
              >
                {option.Name}
                {option?.IsDataLake === 1 &&
                  (option?.Detail.type.toLowerCase() === "s3" ? (
                    <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                  ) : (
                    <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                  ))}
              </MenuItem>
            ))}
          </Select>
          {$hideProperties && (
            <FormHelperText>Please select just 1 file to create table in the database</FormHelperText>
          )}
        </FormControl>
        {!["snowflake", "mysql", "salesforce", "veevavault", ""].includes(destinationType) && (
          <BrowseInput
            exchangeJobState={exchangeJobState}
            setExchangeJobState={setExchangeJobState}
            $destinationList={$destinationList}
            jobPurpose={jobPurpose}
            destinationType={destinationType}
          />
        )}
      </Grid>
      {sqlJobs.includes(sourceType.toLowerCase()) && browserAsTargets.includes(destinationType) && (
        <BrowserAsTarget
          exchangeJobState={exchangeJobState}
          setExchangeJobState={setExchangeJobState}
          previousState={previousState}
        />
      )}
      {((!$hideProperties && ["snowflake", "mysql"].includes(destinationType)) ||
        ["salesforce", "veevavault"].includes(destinationType)) && (
        <Grid size={12}>
          <DestinationPropertiesForm
            loading={fieldLoading}
            exchangeJobState={exchangeJobState}
            setExchangeJobState={setExchangeJobState}
            previousState={previousState}
            destinationType={destinationType}
          />
        </Grid>
      )}
      {["MYSQL", "SNOWFLAKE"].includes(sourceType.toUpperCase()) &&
        destinationType.toUpperCase() === "S3" &&
        !singleFile && (
          <Grid>
            <Alert
              variant="outlined"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              severity="info"
              sx={{ pt: 0, pb: 0, borderRadius: 20 }}
            >
              {`Exporting data from ${
                sourceType.toUpperCase() === "MYSQL" ? `AURORA ${sourceType.toUpperCase()}` : sourceType.toUpperCase()
              } to ${destinationType.toUpperCase()} may generate multiple files.`}
            </Alert>
          </Grid>
        )}
    </Grid>
  );
};

export default React.memo(DestinationForm);
